import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography, 
  Paper, 
  IconButton,
  TextField,
  Grid,
  LinearProgress,
  useTheme,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloseIcon from '@mui/icons-material/Close';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import MovieIcon from '@mui/icons-material/Movie';
import { toast } from 'react-toastify';
import { uploadService, StorageUsage } from '../../services/api/uploadService';
import { useAuth } from '../../contexts/AuthContext';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DropZone = styled(Paper)<{ isDragActive: boolean }>(({ isDragActive }) => ({
  padding: '24px',
  border: `2px dashed ${isDragActive ? '#1976d2' : '#ccc'}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
  backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : '#f9f9f9',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#1976d2',
    backgroundColor: 'rgba(25, 118, 210, 0.04)'
  }
}));

interface MediaUploadProps {
  mediaType: 'image' | 'video' | 'both';
  onUploadSuccess: (url: string, altText?: string) => void;
  initialImageUrl?: string;
  initialAltText?: string;
  label?: string;
  onCancel?: () => void;
  acceptedFileTypes?: string;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  mediaType,
  onUploadSuccess,
  initialImageUrl = '',
  initialAltText = '',
  label = 'Upload Media',
  onCancel,
  acceptedFileTypes = 'image/*,video/*'
}) => {
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaUrl, setMediaUrl] = useState(initialImageUrl);
  const [altText, setAltText] = useState(initialAltText);
  const [error, setError] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragAccept, setIsDragAccept] = useState(false);
  const [isDragReject, setIsDragReject] = useState(false);
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [loadingStorage, setLoadingStorage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { tokens } = useAuth();

  // Load storage usage on component mount
  useEffect(() => {
    const fetchStorageUsage = async () => {
      if (tokens?.idToken) {
        setLoadingStorage(true);
        try {
          const usage = await uploadService.getStorageUsage(tokens.idToken);
          setStorageUsage(usage);
        } catch (error) {
          console.error('Failed to load storage usage:', error);
        } finally {
          setLoadingStorage(false);
        }
      }
    };
    
    fetchStorageUsage();
  }, [tokens?.idToken]);

  // Determine accepted file types based on mediaType prop
  const getAcceptedTypes = useCallback(() => {
    switch (mediaType) {
      case 'image':
        return 'image/*';
      case 'video':
        return 'video/*';
      case 'both':
        return 'image/*,video/*';
      default:
        return 'image/*,video/*';
    }
  }, [mediaType]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await processFile(files[0]);
  };

  const processFile = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      setError('Please upload an image or video file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size should not exceed 10MB');
      return;
    }
    
    // Validate against storage limit
    if (storageUsage && !storageUsage.spaceAvailable) {
      setError('Storage limit reached (5GB). Please delete some files before uploading.');
      return;
    }
    
    if (storageUsage && file.size + storageUsage.totalSizeBytes > 5 * 1024 * 1024 * 1024) {
      setError(`This file would exceed the 5GB storage limit. Current usage: ${storageUsage.usedStorageGB.toFixed(2)}GB, File size: ${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB`);
      return;
    }

    try {
      setIsUploading(true);
      setError('');
      setUploadProgress(0);

      // Simulate progress using intervals
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + Math.random() * 10;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 300);

      // Verify authentication token is available
      if (!tokens?.idToken) {
        setError('Authentication token missing. Please log in again.');
        clearInterval(progressInterval);
        setIsUploading(false);
        return;
      }

      // Upload the file to S3
      const url = await uploadService.upload(file, tokens.idToken, 'resources/');
      
      // Clear interval and set progress to 100%
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Set the media URL and clear any previous error
      setMediaUrl(url);
      
      // Call the callback with the media URL and alt text
      onUploadSuccess(url, altText);

      // Refresh storage usage after successful upload
      const updatedUsage = await uploadService.getStorageUsage(tokens.idToken);
      setStorageUsage(updatedUsage);

      // Show success message
      toast.success('File uploaded successfully');

      // Reset progress after a short delay
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload file');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFile(e.dataTransfer.files[0]);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveMedia = () => {
    setMediaUrl('');
    onUploadSuccess('', '');
  };

  const handleAltTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAltText(e.target.value);
    if (mediaUrl) {
      onUploadSuccess(mediaUrl, e.target.value);
    }
  };

  // Determine if the media is a video based on URL or file type
  const isVideo = mediaUrl && (
    mediaUrl.includes('.mp4') || 
    mediaUrl.includes('.webm') || 
    mediaUrl.includes('.avi') ||
    mediaUrl.includes('youtube.com') || 
    mediaUrl.includes('youtu.be') ||
    mediaUrl.includes('vimeo.com')
  );

  // Helper to check if it's a hosted platform video (YouTube/Vimeo)
  const isHostedVideo = (url: string) => {
    return url.includes('youtube.com') || 
           url.includes('youtu.be') || 
           url.includes('vimeo.com');
  };

  // Helper to render YouTube and Vimeo embeds
  const renderVideoEmbed = (url: string) => {
    // Convert YouTube watch URLs to embed URLs
    if (url.includes('youtube.com/watch')) {
      const videoId = new URL(url).searchParams.get('v');
      return `https://www.youtube.com/embed/${videoId}`;
    }
    // Convert YouTube short URLs
    else if (url.includes('youtu.be')) {
      const videoId = url.split('/').pop();
      return `https://www.youtube.com/embed/${videoId}`;
    }
    // Convert Vimeo URLs
    else if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  // Format bytes to human-readable size
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {onCancel && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload Media
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Upload images or videos to use in your content
        </Typography>
      </Box>
      
      {/* Storage Usage Card */}
      {storageUsage && (
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Storage Usage: {storageUsage.usedStorageGB.toFixed(2)} GB of {storageUsage.totalStorageGB} GB
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={storageUsage.usagePercentage}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: storageUsage.usagePercentage > 90 
                        ? theme.palette.error.main 
                        : storageUsage.usagePercentage > 70
                          ? theme.palette.warning.main
                          : theme.palette.success.main
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    {storageUsage.mediaCount} files ({storageUsage.imageCount} images, {storageUsage.videoCount} videos)
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {storageUsage.remainingStorageGB.toFixed(2)} GB free
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      
      {loadingStorage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Upload area */}
      <Box
        sx={{
          border: '2px dashed',
          borderColor: isDragActive
            ? isDragAccept
              ? 'success.main'
              : isDragReject
                ? 'error.main'
                : 'primary.main'
            : 'divider',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 150,
          backgroundColor: isDragActive ? 'action.hover' : 'transparent',
          position: 'relative',
          cursor: 'pointer',
          transition: '.1s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept={getAcceptedTypes()}
          onChange={handleFileChange}
          disabled={isUploading || (storageUsage ? !storageUsage.spaceAvailable : false)}
        />

        {isUploading ? (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <CircularProgress
              variant="determinate"
              value={uploadProgress}
              size={50}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="textSecondary">
              Uploading... {uploadProgress.toFixed(0)}%
            </Typography>
          </Box>
        ) : (
          <>
            <CloudUploadIcon
              sx={{ fontSize: 40, color: 'action.active', mb: 1 }}
            />
            <Typography variant="body1" mb={1}>
              Drag & drop your file here or click to browse
            </Typography>
            <Typography variant="caption" color="textSecondary" align="center">
              Supported formats: images (JPG, PNG, GIF, etc.) and videos (MP4, etc.)<br />
              Maximum file size: 10MB
            </Typography>
          </>
        )}
      </Box>

      {/* Alt text input field */}
      <TextField
        fullWidth
        margin="normal"
        label="Alt Text (optional)"
        variant="outlined"
        value={altText}
        onChange={handleAltTextChange}
        disabled={isUploading}
        placeholder="Describe this media for accessibility"
        size="small"
      />

      {/* Media preview */}
      {mediaUrl && !isUploading && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="subtitle2" gutterBottom>
            Uploaded Successfully
          </Typography>
          {mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
            <Box
              component="img"
              src={mediaUrl}
              alt={altText || 'Uploaded image'}
              sx={{
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 1,
              }}
            />
          ) : mediaUrl.match(/\.(mp4|webm|avi|mov)$/i) ? (
            <MovieIcon sx={{ fontSize: 60, color: 'primary.main' }} />
          ) : (
            <InsertPhotoIcon sx={{ fontSize: 60, color: 'primary.main' }} />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default MediaUpload; 